// static/Fluid_Schutff_Viscosity_Edition.xlsx
import {
  Oil_Volume_Factor_Options_Enum,
  Bubble_Point_Options_Enum,
  Solution_Gas_Oil_Ratio_Options_Enum
} from "gql/api";
import { Rankine } from "@/libs/units/temperature";
import { API } from "@/libs/units/density";

import { oilPhaseVolumeFactorCalculator as deGhettoHeavyOil } from "./deGhettoHeavyOil";
import { oilPhaseVolumeFactorCalculator as glasoNorthSea } from "./glasoNorthSea";
import { oilPhaseVolumeFactorCalculator as kartoatmodjoSchmidt } from "./kartoatmodjoSchmidt";
import { oilPhaseVolumeFactorCalculator as petroskyFarshadGom } from "./petroskyFarshadGom";
import { oilPhaseVolumeFactorCalculator as standing } from "./standing";
import { oilPhaseVolumeFactorCalculator as vasquezBeggs } from "./vasquezBeggs";
import { oilPhaseVolumeFactorCalculator as lasater } from "./lasater";

import type {
  OilPhasePVTFluidProperties,
  OilPhasePVTFluidPropertyParameters
} from "../d";
import type { Correlations } from "@/composables/forms/formTypes";
import type { BasicData } from "@/libs/calculations/fluidProperties/d";
import type { LagrangeInterpolationMapWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";
import { evaluateLagrangePolynomialWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";

const methodMapBo = {
  [Oil_Volume_Factor_Options_Enum.DeGhettoHeavyOil]: deGhettoHeavyOil,
  [Oil_Volume_Factor_Options_Enum.GlasoNorthSea]: glasoNorthSea,
  [Oil_Volume_Factor_Options_Enum.KartoatmodjoSchmidt]: kartoatmodjoSchmidt,
  [Oil_Volume_Factor_Options_Enum.PetroskyFarshadGom]: petroskyFarshadGom,
  [Oil_Volume_Factor_Options_Enum.Standing]: standing,
  [Oil_Volume_Factor_Options_Enum.VasquezBeggs]: vasquezBeggs
};

const methodMapRso = {
  [Solution_Gas_Oil_Ratio_Options_Enum.Lasater]: lasater,
  [Solution_Gas_Oil_Ratio_Options_Enum.DeGhettoHeavyOil]: deGhettoHeavyOil,
  [Solution_Gas_Oil_Ratio_Options_Enum.GlasoNorthSea]: glasoNorthSea,
  [Solution_Gas_Oil_Ratio_Options_Enum.KartoatmodjoSchmidt]:
    kartoatmodjoSchmidt,
  [Solution_Gas_Oil_Ratio_Options_Enum.PetroskyFarshadGom]: petroskyFarshadGom,
  [Solution_Gas_Oil_Ratio_Options_Enum.Standing]: standing,
  [Solution_Gas_Oil_Ratio_Options_Enum.VasquezBeggs]: vasquezBeggs
};

const methodMapPb = {
  [Bubble_Point_Options_Enum.Lasater]: lasater,
  [Bubble_Point_Options_Enum.DeGhettoHeavyOil]: deGhettoHeavyOil,
  [Bubble_Point_Options_Enum.GlasoNorthSea]: glasoNorthSea,
  [Bubble_Point_Options_Enum.KartoatmodjoSchmidt]: kartoatmodjoSchmidt,
  [Bubble_Point_Options_Enum.PetroskyFarshadGom]: petroskyFarshadGom,
  [Bubble_Point_Options_Enum.Standing]: standing,
  [Bubble_Point_Options_Enum.VasquezBeggs]: vasquezBeggs
};

// static/Fluid_Schutff_Gas_Edition.xlsx

export function getPVTFluidProperties(
  basicData: BasicData,
  gasOilRatio: number,
  correlations: Correlations,
  inSituPressurePSIA: number,
  temperatureF: number,
  condensateApi: number,
  condensateSpecificGravity: number,
  bubblePointPressurePSIAOverride?: number,
  customFluidProperties?: LagrangeInterpolationMapWithGroupings | null,
  waterCutRatio = 0
): OilPhasePVTFluidProperties {
  const temperatureInRankine = Rankine.toThisUnit(temperatureF);
  const tubingPressurePSIA = basicData.bottomTubingPressurePSIA;
  const reservoirPressurePSIA = basicData.staticBottomholePressurePSIA;
  const surfaceTemperatureF = basicData.surfaceTemperatureF;
  const oilAPI = basicData.oilAPI;
  const gasSpecificGravity = basicData.gasSpecificGravity;
  const oilSpecificGravity = API.toStandardUnit(oilAPI);

  const solutionGasRsoMethod =
    correlations?.solution_gor || Solution_Gas_Oil_Ratio_Options_Enum.Standing;
  const bubblePointPressurePSIAMethod =
    correlations?.bubble_point || Bubble_Point_Options_Enum.Standing;
  const oilFormationVolumeFactorSatBoMethod =
    correlations?.oil_volume_factor_saturated ||
    Oil_Volume_Factor_Options_Enum.Standing;
  const oilFormationVolumeFactorUnSatBoMethod =
    correlations?.oil_volume_factor_undersaturated ||
    Oil_Volume_Factor_Options_Enum.Standing;

  const rsoCalculator =
    methodMapRso[solutionGasRsoMethod].solutionGasRsoCalculator;
  const pbCalculator =
    methodMapPb[bubblePointPressurePSIAMethod]
      .bubblePointPressurePSIACalculator;
  const boSatCalculator =
    methodMapBo[oilFormationVolumeFactorSatBoMethod]
      .oilPhaseVolumeFactorBoCalculator;
  const boUnSatCalculator =
    methodMapBo[oilFormationVolumeFactorUnSatBoMethod]
      .oilPhaseVolumeFactorBoCalculator;

  const params: OilPhasePVTFluidPropertyParameters = {
    gasSpecificGravity,
    oilAPI,
    surfaceTemperatureF,
    tubingPressurePSIA,
    reservoirPressurePSIA,
    temperatureInRankine,
    inSituPressurePSIA,
    oilSpecificGravity,
    temperatureF,
    gasOilRatio,
    condensateSpecificGravity,
    gasType: basicData.gasType,
    sourceParams: {
      basicData,
      gasOilRatio,
      correlations,
      inSituPressurePSIA,
      temperatureF,
      bubblePointPressurePSIAOverride
    }
  };

  let solutionGasRso: number = 0;
  let solutionGasRsoInSitu: number = 0;
  let solutionGasRsoAtReservoir: number = 0;
  let rsoIntermediates;

  if (customFluidProperties?.solutionGasOilRatioSCFSTB != null) {
    solutionGasRso = evaluateLagrangePolynomialWithGroupings(
      customFluidProperties.solutionGasOilRatioSCFSTB,
      inSituPressurePSIA,
      temperatureF
    );
    // const c = temperatureF;
    solutionGasRsoInSitu = solutionGasRso;
    solutionGasRsoAtReservoir = solutionGasRso;
  } else {
    ({ solutionGasRsoInSitu, solutionGasRsoAtReservoir, ...rsoIntermediates } =
      rsoCalculator(params));
  }
  // cap the solutionGasRso at the gasOilRatio
  solutionGasRso = Math.min(solutionGasRsoInSitu, gasOilRatio);

  const x =
    7.916 * 10 ** -4 * oilAPI ** 1.541 -
    4.561 * 10 ** -5 * temperatureF ** 1.3911;

  // PVT!AA23 — in situ Rso corrected for GLR: AA5*(1-D46)
  const solutionGasRsoCorrected = solutionGasRso * (1 - waterCutRatio);

  let bubblePointPressurePSIA: number = 0;
  let pbIntermediates;

  if (customFluidProperties?.bubblePointPressurePSIA != null) {
    bubblePointPressurePSIA = evaluateLagrangePolynomialWithGroupings(
      customFluidProperties.bubblePointPressurePSIA,
      inSituPressurePSIA,
      temperatureF
    );
    pbIntermediates = {
      bubblePointPressurePSIA
    };
  } else {
    if (!bubblePointPressurePSIAOverride) {
      pbIntermediates = pbCalculator({
        ...params,
        ...rsoIntermediates,
        solutionGasRsoInSitu,
        solutionGasRsoAtReservoir
      });
      bubblePointPressurePSIA = pbIntermediates.bubblePointPressurePSIA;
      bubblePointPressurePSIA =
        bubblePointPressurePSIA > 14.7 ? bubblePointPressurePSIA : 14.7;
    } else {
      bubblePointPressurePSIA = bubblePointPressurePSIAOverride;
      pbIntermediates = {
        bubblePointPressurePSIAOverride
      };
    }
  }

  // Two Bo calcs are now performed, even if both methods are the same
  // this is to allow for different methods to be used for Sat and UnSat
  // Note that oilFormationVolumeFactorSatBoOverride is passed into the UnSat calc
  // This dependency can only go one direction
  let oilFormationVolumeFactorSatBo: number = 0;
  let oilFormationVolumeFactorUnSatBo: number = 0;
  let satBoIntermediates;
  let unsatBoIntermediates;
  // this decision is the choice that is made for the final oilPhaseVolumeFactorBo for each method
  let oilPhaseVolumeFactorBo: number = 0;

  if (customFluidProperties?.oilFormationVolumeFactorBBLSTB != null) {
    oilPhaseVolumeFactorBo = evaluateLagrangePolynomialWithGroupings(
      customFluidProperties?.oilFormationVolumeFactorBBLSTB,
      inSituPressurePSIA,
      temperatureF
    );
    oilFormationVolumeFactorSatBo = oilPhaseVolumeFactorBo;
    oilFormationVolumeFactorUnSatBo = oilPhaseVolumeFactorBo;
  } else {
    ({ oilFormationVolumeFactorSatBo, ...satBoIntermediates } = boSatCalculator(
      {
        ...params,
        ...rsoIntermediates,
        ...pbIntermediates,
        solutionGasRsoInSitu,
        condensateApi,
        solutionGasRsoAtReservoir,
        bubblePointPressurePSIA
      }
    ));
    ({ oilFormationVolumeFactorUnSatBo, ...unsatBoIntermediates } =
      boUnSatCalculator({
        ...params,
        ...rsoIntermediates,
        ...pbIntermediates,
        solutionGasRsoInSitu,
        condensateApi,
        x,
        solutionGasRsoAtReservoir,
        bubblePointPressurePSIA,
        oilPhaseVolumeFactorBoSatOverride: oilFormationVolumeFactorSatBo
      }));

    if (inSituPressurePSIA > bubblePointPressurePSIA) {
      oilPhaseVolumeFactorBo = oilFormationVolumeFactorUnSatBo;
    } else {
      oilPhaseVolumeFactorBo = oilFormationVolumeFactorSatBo;
    }
  }

  return {
    solutionGasRso,
    solutionGasRsoCorrected,
    bubblePointPressurePSIA,
    oilPhaseVolumeFactorBo,
    oilFormationVolumeFactorSatBo,
    oilFormationVolumeFactorUnSatBo,
    intermediates: {
      rso: rsoIntermediates,
      pb: pbIntermediates,
      satBo: satBoIntermediates,
      unsatBo: unsatBoIntermediates
    },
    params
  };
}
